import { useMutation, useQuery } from '@apollo/client/react';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { last, without } from 'lodash';
import { useContext, useLayoutEffect } from 'react';
import { Rnd } from 'react-rnd';

import { StyledRemoveIssue } from '../StyledComponents';

import DiaryEntryEventsRenderer from './DiaryEntryEventsRenderer';
import DiaryEntryItemStatesRenderer from './DiaryEntryItemStatesRenderer';
import { checkEventOverlap, checkItemStatesOverlap, getColors } from './helperMethods';

import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionContext from '@/Contexts/Permissions';
import { ReportContext } from '@/Contexts/ReportContext';
import {
  type Event,
  type Issue,
  PermissionActionEnum,
  PermissionEntityEnum,
  GetCompanyEventsDocument,
  EventStatusEnum,
  GetDeliveryRegistersDocument,
  type DeliveryAndCollectionItemState,
  RemoveIssueDocument,
  GetReportIssuesDocument,
  UpdateReportIssueDocument,
  IssueTypeEnum,
} from '@/graphql/generated/graphql';
import { toDecimalHours, getInUTC } from '@/utils/dateTimeHelpers';

export type IssueType = {
  __typename?: 'Issue';
  column: number;
  totalColumns: number;
} & Issue;

export type EventType = {
  __typename?: 'Event';
  column: number;
  totalColumns: number;
  isEvent: true;
} & Event;

const iconSx = {
  fontSize: '20px',
  color: 'rgba(0, 0, 0, 0.6)',
  backgroundColor: 'white',
  borderRadius: '50%',
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

let cellSize = 1366 / 24;

interface DiaryEntryIssuesViewProps {
  positionedIssues: Array<IssueType>;
  setSelectedIssue: React.Dispatch<React.SetStateAction<Issue | null>>;
}

const DiaryEntryIssuesView: React.FC<DiaryEntryIssuesViewProps> = ({ positionedIssues, setSelectedIssue }) => {
  const { selectedProject, selectedReport } = useContext(ReportContext);
  const { currentUser } = useContext(CurrentUserContext);
  const { can } = useContext(PermissionContext);
  const { Edit, Manage } = PermissionActionEnum;
  const { Reports } = PermissionEntityEnum;

  const { data: eventData } = useQuery(GetCompanyEventsDocument, {
    skip: currentUser?.defaultCompany?.id === undefined && selectedReport === null,
    variables: {
      companyId: currentUser?.defaultCompany?.id as string,
      date: selectedReport ? selectedReport.reportDate : null,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      status: without(Object.values(EventStatusEnum), EventStatusEnum.Discontinued),
    },
  });

  const positionedEvents = checkEventOverlap(
    eventData?.getCompanyEvents ? eventData?.getCompanyEvents : [],
  ) as EventType[];

  const { data: deliveryRegistersData } = useQuery(GetDeliveryRegistersDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !selectedProject || !selectedReport,
    variables: { projectId: selectedProject?.id as string },
  });

  const extractedStates =
    deliveryRegistersData?.getDeliveryRegisters?.flatMap((register) =>
      register.items.flatMap(
        (item) => last(item.itemStates.filter((state) => state.reportId === selectedReport?.id)) ?? [],
      ),
    ) ?? [];

  const positionedItemStates = checkItemStatesOverlap(extractedStates as DeliveryAndCollectionItemState[]);

  const [removeIssue] = useMutation(RemoveIssueDocument, {
    refetchQueries: [
      {
        query: GetReportIssuesDocument,
        variables: {
          reportId: selectedReport?.id as string,
        },
      },
    ],
  });

  const [updateIssue] = useMutation(UpdateReportIssueDocument, {
    refetchQueries: [
      {
        query: GetReportIssuesDocument,
        variables: {
          reportId: selectedReport?.id as string,
        },
      },
    ],
  });

  const handleEditClick = (issue: IssueType) => {
    if (!can(Edit, Reports, { projectId: selectedProject?.id })) return;
    setSelectedIssue(issue);
  };

  const handleRemoveIssue = (issueId: string) => {
    if (!can(Manage, Reports, { projectId: selectedProject?.id })) return;
    removeIssue({ variables: { issueId: issueId } });
  };

  const toggleIssueType = (issueType: IssueTypeEnum, id: string) => {
    updateIssue({
      variables: {
        reportIssue: {
          id,
          issueType: issueType,
        },
      },
    });
  };

  const getIssueTypeIcon = (issue: IssueType) => {
    if (issue.issueType === IssueTypeEnum.InternalIssue) {
      return <CallReceivedIcon sx={iconSx} onClick={() => toggleIssueType(IssueTypeEnum.ExternalIssue, issue.id)} />;
    }

    if (issue.issueType === IssueTypeEnum.ExternalIssue) {
      return <CallMadeIcon sx={iconSx} onClick={() => toggleIssueType(IssueTypeEnum.InternalIssue, issue.id)} />;
    }
  };

  const getIssueToolTipTitle = (issue: IssueType) => {
    return (
      <Grid container direction="column" spacing={1} width={300}>
        <Typography variant="subtitle1" fontWeight="bold">
          {issue.headline}
        </Typography>
        <Typography
          sx={{
            backgroundColor: 'white',
            p: 1,
            borderRadius: 1,
            color: 'black',
            '& span[data-type="mention"]': {
              fontWeight: 'bold',
              backgroundColor: 'rgba(0, 0, 255, 0.1)',
              padding: '2px 4px',
              borderRadius: '4px',
            },
          }}
          variant="body2"
          dangerouslySetInnerHTML={{ __html: issue.description }}
        />
      </Grid>
    );
  };

  useLayoutEffect(() => {
    if (document && document.getElementById('time')) {
      cellSize = document.getElementById('time')!.clientWidth / 24;
    }
  }, []);

  return (
    <Box sx={{ position: 'relative', flex: 1, zIndex: 1, overflowY: 'auto' }}>
      {/* Render Issues */}
      {positionedIssues.map((issue, index) => (
        <Rnd
          key={`issue-card-${issue.id}-${issue.startTime}-${issue.endTime}`}
          style={{
            color: '#333333',
            backgroundColor: getColors(issue.issueType).backgroundColor,
            borderLeft: '3px solid',
            borderColor: getColors(issue.issueType).borderLeftColor,
            borderRadius: 1,
            transition: 'all 0.2s ease',
            cursor: 'pointer',
          }}
          minHeight={40}
          maxHeight={40}
          disableDragging
          default={{
            x: toDecimalHours(dayjs(issue.startTime)) * cellSize,
            y: index * 50,
            width: `${cellSize * (toDecimalHours(dayjs(issue.endTime)) - toDecimalHours(dayjs(issue.startTime)))}px`,
            height: 40,
          }}
          resizeHandleStyles={{
            topLeft: { display: 'none' },
            topRight: { display: 'none' },
            bottomRight: { display: 'none' },
            bottomLeft: { display: 'none' },
            top: { display: 'none' },
            bottom: { display: 'none' },
          }}
          onResizeStop={(_, direction, _ref, delta) => {
            const incrementInterval = delta.width / (cellSize / 2);
            const minutesToAdd = (incrementInterval * 30).toFixed(2);
            updateIssue({
              variables: {
                reportIssue: {
                  id: issue.id,
                  ...(direction === 'left' && {
                    startTime: getInUTC(dayjs(issue.startTime).add(-parseInt(minutesToAdd), 'minutes')),
                  }),
                  ...(direction === 'right' && {
                    endTime: getInUTC(dayjs(issue.endTime).add(parseInt(minutesToAdd), 'minutes')),
                  }),
                },
              },
            });
          }}
        >
          <Tooltip key={`report-issue-${issue.id}`} title={getIssueToolTipTitle(issue)} placement="top" arrow>
            <Box
              id={`diary-entry-${issue.id}`}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(issue);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px',
                '&:hover': {
                  backgroundColor: getColors(issue.issueType).backgroundHover,
                  transform: 'scale(1.01)',
                  zIndex: 1,
                },
                '&:hover .close-icon': {
                  opacity: 1,
                },
              }}
            >
              <StyledRemoveIssue
                className="close-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveIssue(issue.id);
                }}
              >
                <CloseIcon fontSize="small" />
              </StyledRemoveIssue>
              <Grid container alignItems="center" spacing={1} flexWrap="nowrap">
                {getIssueTypeIcon(issue)}
                <Typography variant="body2" noWrap>
                  {' '}
                  {issue.headline}
                </Typography>
              </Grid>
            </Box>
          </Tooltip>
        </Rnd>
      ))}

      {/* Render Events */}
      <DiaryEntryEventsRenderer
        positionedEvents={positionedEvents}
        issuesCount={positionedIssues.length}
        cellSize={cellSize}
      />

      {/* Render Item States */}
      <DiaryEntryItemStatesRenderer
        positionedItemStates={positionedItemStates}
        issuesCount={positionedIssues.length}
        eventsCount={positionedEvents.length}
        cellSize={cellSize}
      />
    </Box>
  );
};

export default DiaryEntryIssuesView;
